import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { Camera, Check, Loader2, MapPinned, Plus, Receipt, Route, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, hoyISO } from "@/lib/format";
import { useEmpresa } from "@/hooks/useEmpresa";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import RutaRoadmapVisual from "@/components/RutaRoadmapVisual";
import ClienteBusquedaSelect from "@/components/ClienteBusquedaSelect";
import MapaSelectorPunto from "@/components/MapaSelectorPunto";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function addDaysISO(iso, n) {
  const [y, m, d] = (iso || hoyISO()).split("-").map(Number);
  const dt = new Date(y, m - 1, d + n);
  const pad = (x) => String(x).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

export default function MisRutas() {
  const { empresa, cargando: cargandoEmpresa } = useEmpresa();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ruta, setRuta] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [parada, setParada] = useState(null);
  const [tab, setTab] = useState("anotaciones");
  const [notas, setNotas] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [gastoForm, setGastoForm] = useState({ categoria: "peaje_parking", importe: "", notas: "" });
  const fileRef = useRef(null);
  const gastoFileRef = useRef(null);
  const [gps, setGps] = useState({ lat: null, lng: null });
  const [clientes, setClientes] = useState([]);
  const [crearOpen, setCrearOpen] = useState(false);
  const [tituloNueva, setTituloNueva] = useState("");
  const [fechaNueva, setFechaNueva] = useState(hoyISO());
  const [paradasNueva, setParadasNueva] = useState([]);
  const [otrasRutas, setOtrasRutas] = useState([]);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [{ data }, cli, list] = await Promise.all([
        api.get("/rutas-comerciales/hoy"),
        api.get("/rutas-comerciales/clientes", { params: { activos: true } }).catch(() => ({ data: { items: [] } })),
        api.get("/rutas-comerciales", { params: { desde: hoyISO() } }).catch(() => ({ data: { items: [] } })),
      ]);
      setRuta(data.ruta);
      setClientes(cli.data?.items || []);
      setOtrasRutas((list.data?.items || []).filter((r) => r.estado !== "cancelada"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    if (!cargandoEmpresa) cargar();
  }, [cargandoEmpresa, cargar]);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, []);

  const abrirParada = (p, seccion = "anotaciones") => {
    setParada(p);
    setTab(seccion);
    setNotas(p.notas_visita || "");
    setGastoForm({ categoria: "peaje_parking", importe: "", notas: "" });
  };

  const refrescarDetalle = async () => {
    const { data } = await api.get(`/rutas-comerciales/${ruta.id}`);
    setRuta(data);
    if (parada) {
      const p = (data.paradas || []).find((x) => x.id === parada.id);
      if (p) setParada(p);
    }
  };

  const yaEnRutaActual = useMemo(() => {
    const conteo = new Map();
    for (const p of ruta?.paradas || []) {
      if (p.cliente_id == null) continue;
      const id = String(p.cliente_id);
      conteo.set(id, (conteo.get(id) || 0) + 1);
    }
    return conteo;
  }, [ruta?.paradas]);

  const yaEnRutaNueva = useMemo(() => {
    const conteo = new Map();
    for (const p of paradasNueva) {
      if (p.cliente_id == null) continue;
      const id = String(p.cliente_id);
      conteo.set(id, (conteo.get(id) || 0) + 1);
    }
    return conteo;
  }, [paradasNueva]);

  const anadirClienteARutaActual = async (c) => {
    if (!ruta) return;
    setGuardando(true);
    try {
      const { data } = await api.post(`/rutas-comerciales/${ruta.id}/paradas`, { cliente_id: c.id });
      setRuta(data);
      toast.success("Cliente añadido. Encargado y administración avisados por correo.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardando(false);
    }
  };

  const anadirClienteANueva = (c) => {
    setParadasNueva((prev) => [
      ...prev,
      { cliente_id: c.id, nombre: c.nombre, direccion: c.direccion || "" },
    ]);
  };

  const fechasOcupadas = useMemo(() => {
    const s = new Set(otrasRutas.map((r) => r.fecha));
    if (ruta?.fecha) s.add(ruta.fecha);
    return s;
  }, [otrasRutas, ruta?.fecha]);

  const fechaYaTieneRuta = fechasOcupadas.has(fechaNueva);

  const abrirCrearRuta = () => {
    const ocupadas = new Set(otrasRutas.map((r) => r.fecha));
    if (ruta?.fecha) ocupadas.add(ruta.fecha);
    let f = hoyISO();
    while (ocupadas.has(f)) f = addDaysISO(f, 1);
    setFechaNueva(f);
    setTituloNueva("");
    setParadasNueva([]);
    setCrearOpen(true);
  };

  const abrirRuta = async (id) => {
    setCargando(true);
    try {
      const { data } = await api.get(`/rutas-comerciales/${id}`);
      setRuta(data);
      setParada(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  };

  const crearRutaNueva = async () => {
    if (!paradasNueva.length) return toast.error("Añade al menos un cliente");
    if (!fechaNueva) return toast.error("Indica la fecha de la ruta");
    if (fechaYaTieneRuta) return toast.error("Ese día ya tienes una ruta. Elige otra fecha.");
    setGuardando(true);
    try {
      const { data } = await api.post("/rutas-comerciales", {
        usuario_id: user?.id,
        fecha: fechaNueva,
        titulo: tituloNueva.trim() || "Mi ruta",
        paradas: paradasNueva.map((p, i) => ({
          cliente_id: p.cliente_id,
          nombre: p.nombre,
          direccion: p.direccion || "",
          orden: i + 1,
        })),
      });
      setCrearOpen(false);
      setParadasNueva([]);
      setTituloNueva("");
      toast.success("Ruta creada. Encargado y administración avisados por correo.");
      setRuta(data);
      const list = await api.get("/rutas-comerciales", { params: { desde: hoyISO() } }).catch(() => ({ data: { items: [] } }));
      setOtrasRutas((list.data?.items || []).filter((r) => r.estado !== "cancelada"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardando(false);
    }
  };

  const marcarVisitada = async () => {
    setGuardando(true);
    try {
      await api.put(`/rutas-comerciales/paradas/${parada.id}`, {
        estado: "visitada",
        notas_visita: notas,
        latitud: gps.lat ?? parada.latitud,
        longitud: gps.lng ?? parada.longitud,
      });
      toast.success("Visita registrada. Supervisores notificados.");
      await refrescarDetalle();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardando(false);
    }
  };

  const guardarNotas = async () => {
    setGuardando(true);
    try {
      await api.put(`/rutas-comerciales/paradas/${parada.id}`, { notas_visita: notas });
      toast.success("Anotación guardada. Visita marcada como realizada.");
      await refrescarDetalle();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardando(false);
    }
  };

  const guardarUbicacion = async (lat, lng) => {
    setGuardando(true);
    try {
      await api.put(`/rutas-comerciales/paradas/${parada.id}`, { latitud: lat, longitud: lng });
      toast.success("Ubicación actualizada");
      await refrescarDetalle();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardando(false);
    }
  };

  const subirAdjunto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("archivo", file);
    fd.append("tipo", file.type === "application/pdf" ? "documento" : "foto");
    setGuardando(true);
    try {
      await api.post(`/rutas-comerciales/paradas/${parada.id}/adjuntos`, fd);
      toast.success("Archivo adjuntado. Visita marcada como realizada.");
      await refrescarDetalle();
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardando(false);
    }
  };

  const eliminarAdjunto = async (adjuntoId, nombre) => {
    if (!window.confirm(`¿Eliminar «${nombre || "este archivo"}»? Podrás subir otro después.`)) return;
    setGuardando(true);
    try {
      await api.delete(`/rutas-comerciales/adjuntos/${adjuntoId}`);
      toast.success("Archivo eliminado");
      await refrescarDetalle();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardando(false);
    }
  };

  const contextoGastoParada = () => ({
    ruta_parada_id: parada.id,
    ruta_id: ruta.id,
    cliente_id: parada.cliente_id,
    nombre: parada.nombre,
    direccion: parada.direccion,
    latitud: parada.latitud ?? gps.lat,
    longitud: parada.longitud ?? gps.lng,
    categoria: gastoForm.categoria,
    importe: gastoForm.importe,
    motivo_visita: `Ruta ${ruta.fecha} · ${parada.nombre}`,
    volver_a: "/mis-rutas",
  });

  const registrarGasto = async (archivo = null) => {
    if (!parada) return;
    const cat = gastoForm.categoria;
    if (cat === "kilometraje") {
      sessionStorage.setItem("gasto_ruta_parada", JSON.stringify({
        ...contextoGastoParada(),
        categoria: "kilometraje",
      }));
      toast.info("Completa el kilometraje del día con esta visita.");
      navigate("/mis-gastos");
      return;
    }
    const necesitaFoto = true;
    if (necesitaFoto && !archivo) {
      gastoFileRef.current?.click();
      return;
    }
    const fd = new FormData();
    fd.append("categoria", cat);
    fd.append("fecha_gasto", hoyISO());
    fd.append("ruta_parada_id", String(parada.id));
    if (parada.cliente_id) fd.append("cliente_id", String(parada.cliente_id));
    if (gastoForm.importe) fd.append("importe", gastoForm.importe);
    if (gastoForm.notas) fd.append("notas", gastoForm.notas);
    fd.append("proveedor", parada.nombre || "Visita comercial");
    fd.append("motivo_visita", `Ruta ${ruta.fecha} · ${parada.nombre}`);
    if (gps.lat != null) {
      fd.append("latitud", String(gps.lat));
      fd.append("longitud", String(gps.lng));
    } else if (parada.latitud != null) {
      fd.append("latitud", String(parada.latitud));
      fd.append("longitud", String(parada.longitud));
    }
    if (cat === "kilometraje") {
      fd.append("origen_direccion", "Empresa / inicio de jornada");
      fd.append("destino_direccion", parada.direccion || parada.nombre || "");
    }
    if (archivo) fd.append("imagen", archivo);

    setGuardando(true);
    try {
      await api.post("/gastos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Gasto registrado. Visita marcada como realizada.");
      setParada(null);
      await cargar();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.codigo === "gasto_duplicado") {
        if (window.confirm(`${detail.mensaje}\n¿Registrar de todas formas?`)) {
          fd.append("forzar_duplicado", "true");
          try {
            await api.post("/gastos", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Gasto registrado. Visita marcada como realizada.");
            setParada(null);
            await cargar();
            return;
          } catch (e2) {
            toast.error(formatApiError(e2));
          }
        }
      } else if (String(detail || "").includes("fotografía") || String(detail || "").includes("justificante")) {
        sessionStorage.setItem("gasto_ruta_parada", JSON.stringify(contextoGastoParada()));
        toast.info("Adjunta la foto del ticket para completar el gasto.");
        navigate("/mis-gastos");
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setGuardando(false);
      if (gastoFileRef.current) gastoFileRef.current.value = "";
    }
  };

  if (!cargandoEmpresa && empresa && !empresa.gestionar_rutas_comerciales) {
    return <Navigate to="/fichar" replace />;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Route}
        title={ruta && ruta.fecha !== hoyISO() ? "Mi ruta" : "Mi ruta de hoy"}
        subtitle="Visitas planificadas, anotaciones, gastos y documentos"
        sectionKey="rutas"
        brandGradient={false}
      >
        <Button onClick={abrirCrearRuta} variant="secondary" className="bg-white text-rose-700 hover:bg-rose-50">
          <Plus className="mr-1 h-4 w-4" /> Crear ruta
        </Button>
      </PageHeader>

      {cargando ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-rose-500" /></div>
      ) : !ruta ? (
        <div className="rounded-2xl border border-dashed border-zinc-200 bg-white py-16 text-center">
          <p className="text-sm text-zinc-500">No tienes ruta comercial asignada para hoy.</p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <Button onClick={abrirCrearRuta}>
              <Plus className="mr-1 h-4 w-4" /> Crear mi ruta
            </Button>
            <Button asChild variant="outline">
              <Link to="/fichar">Volver a fichar</Link>
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-rose-100 bg-rose-50/50 px-4 py-3">
            <div>
              <p className="font-semibold text-zinc-900">{ruta.titulo}</p>
              <p className="text-xs text-zinc-600">
                {fmtFecha(ruta.fecha)} · {ruta.paradas_visitadas}/{ruta.num_paradas} visitadas · {ruta.estado}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="bg-rose-600 text-white">{ruta.num_paradas} paradas</Badge>
              {ruta.fecha !== hoyISO() && otrasRutas.some((r) => r.fecha === hoyISO()) && (
                <Button size="sm" variant="outline" onClick={() => {
                  const hoy = otrasRutas.find((r) => r.fecha === hoyISO());
                  if (hoy) abrirRuta(hoy.id);
                }}>
                  Ver ruta de hoy
                </Button>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-zinc-200 bg-white p-3">
            <Label className="text-xs text-zinc-500">Añadir cliente a la ruta</Label>
            <ClienteBusquedaSelect
              clientes={clientes}
              yaEnRuta={yaEnRutaActual}
              disabled={guardando || ruta.estado === "cancelada"}
              onSelect={anadirClienteARutaActual}
            />
            <p className="mt-1 text-[11px] text-zinc-500">
              Encargado y administración recibirán un correo con los clientes que añadas.
            </p>
          </div>

          <RutaRoadmapVisual
            ruta={ruta}
            paradaActivaId={parada?.id}
            onOpenParada={abrirParada}
          />
        </>
      )}

      {otrasRutas.filter((r) => r.id !== ruta?.id).length > 0 && (
        <div className="rounded-xl border border-zinc-200 bg-white p-3">
          <p className="mb-2 text-xs font-semibold text-zinc-600">Otras rutas</p>
          <ul className="space-y-1">
            {otrasRutas.filter((r) => r.id !== ruta?.id).map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => abrirRuta(r.id)}
                  className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-zinc-700 hover:bg-rose-50"
                >
                  <span className="truncate">{fmtFecha(r.fecha)} · {r.titulo || "Ruta"}</span>
                  <span className="text-[11px] text-zinc-500">{r.num_paradas} paradas</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog
        open={crearOpen}
        onOpenChange={(v) => {
          setCrearOpen(v);
          if (!v) {
            setParadasNueva([]);
            setTituloNueva("");
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nueva ruta comercial</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Fecha</Label>
                <Input
                  type="date"
                  min={hoyISO()}
                  value={fechaNueva}
                  onChange={(e) => setFechaNueva(e.target.value)}
                />
              </div>
              <div>
                <Label>Título</Label>
                <Input
                  value={tituloNueva}
                  onChange={(e) => setTituloNueva(e.target.value)}
                  placeholder="Ruta zona norte…"
                />
              </div>
            </div>
            <p className="text-[11px] text-zinc-500">
              {fechaYaTieneRuta
                ? "Ese día ya tienes una ruta: ábrela para añadir clientes, o elige otra fecha."
                : "Puedes crear la ruta de hoy o de un día posterior. Un día, una ruta."}
            </p>
            <div>
              <Label>Añadir cliente</Label>
              <ClienteBusquedaSelect
                clientes={clientes}
                yaEnRuta={yaEnRutaNueva}
                onSelect={anadirClienteANueva}
              />
            </div>
            {paradasNueva.length > 0 && (
              <ul className="space-y-1 rounded-lg border border-zinc-100 p-2">
                {paradasNueva.map((p, i) => (
                  <li key={`${p.cliente_id}-${i}`} className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate">{String(i + 1).padStart(2, "0")}. {p.nombre}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-rose-600"
                      onClick={() => setParadasNueva((prev) => prev.filter((_, j) => j !== i))}
                    >
                      Quitar
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[11px] text-zinc-500">
              Encargado y administración recibirán un correo con los clientes de esta ruta.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCrearOpen(false)}>Cancelar</Button>
            <Button disabled={guardando || fechaYaTieneRuta} onClick={crearRutaNueva}>Crear y notificar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!parada} onOpenChange={(v) => !v && setParada(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {parada && `${String(parada.orden).padStart(2, "0")}. ${parada.nombre}`}
            </DialogTitle>
          </DialogHeader>
          {parada && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {["anotaciones", "gastos", "documentos", "ubicacion"].map((t) => (
                  <Button
                    key={t}
                    size="sm"
                    variant={tab === t ? "default" : "outline"}
                    onClick={() => setTab(t)}
                    className={tab === t ? "bg-rose-600 hover:bg-rose-700" : ""}
                  >
                    {t}
                  </Button>
                ))}
              </div>

              {tab === "anotaciones" && (
                <div className="space-y-3">
                  <Label>Notas de la visita</Label>
                  <Textarea rows={4} value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Resultado, próximos pasos…" />
                  <div className="flex flex-wrap gap-2">
                    <Button disabled={guardando} onClick={guardarNotas}>Guardar anotación</Button>
                    <Button disabled={guardando} className="bg-emerald-600 hover:bg-emerald-700" onClick={marcarVisitada}>
                      <Check className="mr-1 h-4 w-4" /> Marcar visitada
                    </Button>
                  </div>
                </div>
              )}

              {tab === "gastos" && (
                <div className="space-y-3">
                  {(parada.gastos || []).map((g) => (
                    <div key={g.id} className="flex justify-between rounded-lg border border-zinc-100 px-3 py-2 text-sm">
                      <span>{g.categoria}</span>
                      <span className="font-medium">{g.importe != null ? `${Number(g.importe).toFixed(2)} €` : "—"}</span>
                    </div>
                  ))}
                  <div>
                    <Label>Categoría</Label>
                    <Select value={gastoForm.categoria} onValueChange={(v) => setGastoForm({ ...gastoForm, categoria: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="peaje_parking">Peaje / parking</SelectItem>
                        <SelectItem value="dieta">Dieta</SelectItem>
                        <SelectItem value="alojamiento">Alojamiento</SelectItem>
                        <SelectItem value="kilometraje">Kilometraje</SelectItem>
                        <SelectItem value="representacion">Representación</SelectItem>
                        <SelectItem value="ticket">Ticket / otros</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <input
                    ref={gastoFileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf,.pdf"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => registrarGasto(e.target.files?.[0] || null)}
                  />
                  <Button disabled={guardando} className="w-full gap-2" onClick={() => registrarGasto()}>
                    {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Receipt className="h-4 w-4" />}
                    {gastoForm.categoria === "kilometraje"
                      ? "Registrar kilometraje"
                      : "Registrar gasto (solo foto del ticket)"}
                  </Button>
                  <p className="text-[11px] text-zinc-500">
                    Cliente y visita se rellenan solos. Solo hace falta la foto del ticket; el importe lo detecta el OCR.
                    La visita quedará como realizada y volverás al planning.
                  </p>
                  {empresa?.gestionar_gastos && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        sessionStorage.setItem("gasto_ruta_parada", JSON.stringify(contextoGastoParada()));
                        navigate("/mis-gastos");
                      }}
                    >
                      Ir a Mis gastos
                    </Button>
                  )}
                </div>
              )}

              {tab === "documentos" && (
                <div className="space-y-3">
                  {(parada.adjuntos || []).length === 0 && (
                    <p className="text-sm text-zinc-500">Aún no hay fotos ni documentos en esta parada.</p>
                  )}
                  {(parada.adjuntos || []).map((a) => (
                    <div
                      key={a.id}
                      className="flex items-center gap-2 rounded-lg border border-zinc-100 px-3 py-2 text-sm"
                    >
                      <a
                        href={a.url}
                        target="_blank"
                        rel="noreferrer"
                        className="min-w-0 flex-1 truncate hover:underline"
                      >
                        {a.nombre_archivo}
                      </a>
                      <Badge variant="outline">{a.tipo}</Badge>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                        disabled={guardando}
                        title="Eliminar archivo"
                        onClick={() => eliminarAdjunto(a.id, a.nombre_archivo)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={(e) => subirAdjunto(e.target.files?.[0])}
                  />
                  <Button
                    variant="outline"
                    className="w-full gap-2"
                    disabled={guardando}
                    onClick={() => fileRef.current?.click()}
                  >
                    <Camera className="mr-1 h-4 w-4" />
                    <Upload className="h-4 w-4" /> Foto o documento
                  </Button>
                  <p className="text-[11px] text-zinc-500">
                    Si te equivocas o la foto sale mal, elimínala con el icono de la papelera y sube otra.
                  </p>
                </div>
              )}

              {tab === "ubicacion" && (
                <div className="space-y-3">
                  <p className="text-sm text-zinc-600">{parada.direccion || "Sin dirección"}</p>
                  <MapaSelectorPunto
                    lat={parada.latitud}
                    lng={parada.longitud}
                    onChange={guardarUbicacion}
                  />
                  <div className="flex flex-wrap gap-2">
                    {parada.maps_url && (
                      <Button asChild variant="outline" className="gap-1">
                        <a href={parada.maps_url} target="_blank" rel="noreferrer">
                          <MapPinned className="h-4 w-4" /> Google Maps
                        </a>
                      </Button>
                    )}
                    {gps.lat != null && (
                      <Button variant="outline" onClick={() => guardarUbicacion(gps.lat, gps.lng)}>
                        Usar mi GPS actual
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setParada(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
